import { forwardRef } from "react";
import Button, { type ButtonProps } from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import tinycolor from "tinycolor2";
import { mergeSx } from "./mui-utilities";

export type UiButtonProps = Omit<ButtonProps, "translate"> & {
  colorHex?: string;
  loading?: boolean;
};

export const buttonColorStyles = (
  colorHex: string | undefined,
  disabled: boolean | undefined,
  variant: ButtonProps["variant"],
): SystemStyleObject<Theme> => {
  if (!colorHex || disabled) {
    return {};
  }

  const textColor = tinycolor(colorHex).isLight()
    ? "var(--color-text--light)"
    : "var(--color-text--dark)";

  if (variant === "outlined") {
    return {
      "borderColor": colorHex,
      "color": colorHex,
      "&:hover": {
        backgroundColor: `${colorHex}14`,
        borderColor: colorHex,
      },
    };
  }

  return {
    backgroundColor: colorHex,
    color: textColor,
  };
};

const UiButton = forwardRef<HTMLButtonElement, UiButtonProps>(function UiButton(
  { colorHex, disabled, loading, startIcon, sx, ...rest },
  ref,
) {
  const resolvedStartIcon =
    loading || startIcon ? (
      <>
        {startIcon}
        {loading ? (
          <CircularProgress size={14} sx={{ ml: "1px", mr: "5px" }} />
        ) : null}
      </>
    ) : undefined;

  return (
    <Button
      ref={ref}
      {...rest}
      color={colorHex ? undefined : rest.color}
      disabled={disabled}
      startIcon={resolvedStartIcon}
      sx={mergeSx(buttonColorStyles(colorHex, disabled, rest.variant), sx)}
    />
  );
});

export default UiButton;
